import { useState, useEffect, useCallback } from 'react';

interface TabStateOptions {
	key: string;
	defaultValue?: number;
	ttlMinutes?: number;
}

interface StoredTabState {
	value: number;
	timestamp: number;
}

/**
 * Custom hook for persisting tab state with TTL
 * @param key - Unique key for storing tab state
 * @param defaultValue - Default tab index (default: 0)
 * @param ttlMinutes - Time to live in minutes (default: 30)
 */
export function useTabState({ 
	key, 
	defaultValue = 0, 
	ttlMinutes = 30 
}: TabStateOptions) {
	const [tabValue, setTabValue] = useState<number>(() => {
		try {
			const stored = localStorage.getItem(`tab-state-${key}`);
			if (stored) {
				const parsedState: StoredTabState = JSON.parse(stored);
				const now = Date.now();
				const expirationTime = parsedState.timestamp + (ttlMinutes * 60 * 1000);
				
				// Check if the stored state is still valid
				if (now < expirationTime) {
					return parsedState.value;
				} else {
					// Remove expired state
					localStorage.removeItem(`tab-state-${key}`);
				}
			}
		} catch (error) {
			console.warn(`Failed to load tab state for key "${key}":`, error);
			localStorage.removeItem(`tab-state-${key}`);
		}
		
		return defaultValue;
	});

	const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
		setTabValue(newValue);
		
		// Persist to localStorage with timestamp
		try {
			const stateToStore: StoredTabState = {
				value: newValue,
				timestamp: Date.now()
			};
			localStorage.setItem(`tab-state-${key}`, JSON.stringify(stateToStore));
		} catch (error) {
			console.warn(`Failed to save tab state for key "${key}":`, error);
		}
	}, [key]);

	// Cleanup expired states on mount
	useEffect(() => {
		try {
			const keysToRemove: string[] = [];
			for (let i = 0; i < localStorage.length; i++) {
				const storageKey = localStorage.key(i);
				if (storageKey?.startsWith('tab-state-')) {
					const stored = localStorage.getItem(storageKey);
					if (stored) {
						const parsedState: StoredTabState = JSON.parse(stored);
						const now = Date.now();
						const expirationTime = parsedState.timestamp + (ttlMinutes * 60 * 1000);
						
						if (now >= expirationTime) {
							keysToRemove.push(storageKey);
						}
					}
				}
			}
			
			// Remove expired states
			keysToRemove.forEach(key => localStorage.removeItem(key));
		} catch (error) {
			console.warn('Failed to cleanup expired tab states:', error);
		}
	}, [ttlMinutes]);

	return {
		tabValue,
		handleTabChange
	};
}

export default useTabState;